/**
 * Short-lived particle effect entity.
 * Drifts upward, cycles animation frames, fades out, and expires.
 */

import type { Sprite } from "../sprites/SpriteSheet.js";
import { Entity } from "./Entity.js";
import { depthKey } from "../engine/IsoMath.js";

export class EffectEntity extends Entity {
  private sprite: Sprite;
  private frameIndex = 0;
  private frameTicks = 0;
  private ticksPerFrame: number;
  private lifetime: number;
  private maxLifetime: number;
  private driftX: number;
  private driftY: number;
  private alpha = 1;

  /** When true, the effect has expired and should be removed. */
  expired = false;

  constructor(
    id: string,
    sprite: Sprite,
    worldX: number,
    worldY: number,
    options?: {
      lifetime?: number;
      driftX?: number;
      driftY?: number;
      ticksPerFrame?: number;
    },
  ) {
    // Effects use world coords directly, not grid — pass dummy grid values
    super(id, 0, 0, 3);
    this.worldX = worldX;
    this.worldY = worldY;
    this.depth = depthKey(999, 999, 3); // always on top
    this.sprite = sprite;
    this.lifetime = options?.lifetime ?? 2;
    this.maxLifetime = this.lifetime;
    this.driftX = options?.driftX ?? 0;
    this.driftY = options?.driftY ?? -15; // float upward
    this.ticksPerFrame = options?.ticksPerFrame ?? 8;
  }

  update(dt: number): void {
    this.lifetime -= dt;
    if (this.lifetime <= 0) {
      this.expired = true;
      this.visible = false;
      return;
    }

    // Drift
    this.worldX += this.driftX * dt;
    this.worldY += this.driftY * dt;

    // Frame animation
    this.frameTicks++;
    if (this.frameTicks >= this.ticksPerFrame) {
      this.frameTicks = 0;
      this.frameIndex = (this.frameIndex + 1) % this.sprite.frameCount;
    }

    // Fade out in last 30% of lifetime
    const fadeThreshold = this.maxLifetime * 0.3;
    if (this.lifetime < fadeThreshold) {
      this.alpha = Math.max(0, this.lifetime / fadeThreshold);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.alpha < 0.01) return;

    ctx.globalAlpha = this.alpha;
    const frame = this.sprite.frames[this.frameIndex];
    ctx.drawImage(
      frame,
      this.worldX - this.sprite.originX,
      this.worldY - this.sprite.originY,
    );
    ctx.globalAlpha = 1;
  }
}
